// Copy for the Orders 👍/👎 report (src/scenes/Reports/NpsReport). Its own
// namespace rather than a sibling in feedbacks.ts, which belongs to /responses.
// The shared table's own strings (column headers, the row icon aria-labels, the
// footer, the location select) live in W0's `components.feedback_table` /
// `components.table_toolbar` / `components.table_pagination` — NOT duplicated here.
const orderRatings = {
  // This page split in two: the report (Looker embed, this page's own
  // header) moved to /order_ratings behind its own permission gate, while
  // the inbox/detail/notification copy below stayed here and is now
  // consumed by the Order feedback page's Feedback tab instead (a naming
  // artifact of the pre-split file layout — renaming this whole namespace is
  // deferred to keep the wiring change scoped).
  header: {
    title: 'Order ratings',
    description:
      'Order satisfaction is gathered directly in app and web ordering experiences where orders are placed.',
  },
  // AI-1284 relocated the order-feedback-owned notification-settings modal
  // (a de-escalated fork of recovery_ai.settings.escalation_contacts — same
  // mechanics, own copy) into the Settings tab's Notifications sub-tab
  // (settings.notifications below). The modal is gone, but its recipient
  // labels and its error/unavailable copy are unchanged by the new design and
  // stayed on this namespace rather than move — see settings.notifications'
  // own header comment.
  notification_settings: {
    fallback_label:
      'Fallback recipient for locations without a location manager',
    all_label: 'Recipient for all order feedback',
    additional_label: 'Additional recipients (optional)',
    // AI-1284: the collapsed primary-picker badge for an admin recipient
    // ("ACCOUNT ADMIN" in the design, uppercased via the shared Badge's CSS).
    // Deliberately its own key, not a reuse of recovery_ai's admin_badge
    // ('ADMIN') — different copy, different namespace, three other surfaces
    // still render that one unchanged.
    account_admin_badge: 'Account admin',
    save_error:
      'We couldn’t save your order feedback notifications. Please try again.',
    notify_save_error:
      'Failed to update the location-manager notification setting. Please try again.',
    unavailable_notice:
      'Order feedback contact settings aren’t available right now. You can review who’s listed, but saving is disabled until this loads.',
    unloadable_notice:
      'We couldn’t load your current order feedback contacts, so they can’t be changed right now. Refresh the page to try again.',
  },
  // The Settings tab's own left sub-nav and the panes behind it. Each `title`
  // is used twice on purpose: once as the sub-nav item, once as the pane's own
  // <h3> — they are the same words in the design, so they are one string here.
  settings: {
    tablist_label: 'Order feedback settings sections',
    publish: 'Publish',
    // No `publish_hint` here on purpose. The design's bar reads
    // "Ready to go live? Hit the “Publish” button to save changes and push them
    // to your app or website." with the lead-in bold, which is exactly what
    // PublishBanner composes from `cms.components.publish_banner` when no
    // `message` prop overrides it. A copy on this namespace could only drift
    // from that one.
    // FR-60's edit gate. Changing anything on this tab needs the
    // `merchant_settings` MANAGE permission — the same one the server already
    // enforces on `PATCH /merchants/:id`. A viewer without it keeps the whole
    // tab: every sub-tab and every current value. They just cannot change
    // anything.
    edit_permission: {
      // Pane-wide BODY COPY, not a tooltip. The shared tooltip reason
      // (usePermissionReason) is gated behind `rbac-permission-tooltips` and
      // only appears on hover or focus, so a tooltip alone would leave a
      // sighted viewer with a greyed control and no stated reason.
      banner_headline: 'You can view these settings, but not change them.',
      // `%{label}` resolves live from the backend's grouped-permissions payload
      // (usePermissionResourceLabel), so a display_name rename there doesn't
      // leave this naming a permission that no longer exists by that name.
      banner_body:
        'Editing them requires the "%{label}" permission. Contact your admin to update your permission settings.',
      // Fallback for when the grouped-permissions query hasn't resolved (or the
      // RBAC flag is off) and there is no live label to interpolate. Mirrors the
      // backend's `merchant_settings` resource display_name, seeded in
      // nucleus/lib/rbac_seeder.rb — grep there if this ever needs to change.
      label_fallback: 'Merchant Settings',
    },
    feedback_collection: {
      title: 'Feedback collection',
      description: 'Control what guests are asked after they rate an order.',
      // FR-22's "Your current setup" card (AI-1268), below the written-feedback
      // toggle in this sub-tab. Six states; "not collecting" renders no card at
      // all and so has no copy here. Which state applies is decided in
      // SettingsPane/setupCardState.ts, never in this file.
      current_setup: {
        // Already cased as it renders; the card's small-caps rule leaves it
        // unchanged.
        badge: 'YOUR CURRENT SETUP',
        // The RecoveryAI chip draws the brand wordmark as an image and spells
        // out only the rest of the sentence, so the brand keeps its own casing
        // and letterforms instead of being flattened by that uppercase rule.
        // The alt text carries the name for screen readers; the two together
        // read "RecoveryAI is responding automatically".
        recovery_ai_badge_wordmark_alt: 'RecoveryAI',
        recovery_ai_badge: 'is responding automatically',
        means_label: 'WHAT THIS MEANS FOR YOU',
        // Each link renders its own trailing arrow from the icon library, so
        // no arrow glyph belongs in these strings.
        links: {
          view_all_feedback: 'View all feedback',
          manage_recovery_ai: 'Manage RecoveryAI',
        },
        thanx: {
          title: 'Collecting feedback with Thanx',
          body: "Every thumbs-down lands in your inbox. Reply and resolve in two clicks, and attach a reward when it's warranted.",
        },
        // `%{active}` is N and `%{remaining}` is M - N, both derived at the
        // call site, never carried in pre-computed.
        //
        // `body` is a FOUR-KEY MATRIX, because the sentence carries TWO
        // independent plural axes:
        //   - `%{active}`    — "location resolves" / "locations resolve"
        //   - `%{remaining}` — "still sends"       / "still send"
        // react-redux-i18n pluralizes on ONE `%{count}` per key, so a native
        // pair can only ever get one of the two right. The component picks the
        // key instead (`pilotBodyKey` in CurrentSetupCard.tsx) and passes NO
        // `count` replacement — passing one would make the library append its
        // own `_plural` to the already-chosen key and render the raw key
        // string. `_one`/`_other` is not an option either, for the same reason.
        //
        // Naming extends the repo's `_plural` suffix one axis at a time: the
        // base key is both-singular, and each axis that goes plural appends
        // `_<axis>_plural`. All four combinations are reachable — the resolver
        // only requires `0 < active < total`, so 1 of 2 is an ordinary pilot.
        //
        // Grammar only: the wording is verbatim from the design in all four.
        //
        // `body_secondary` stays flat: it reads correctly at 1 ("Turn on the
        // other 1 in a couple of clicks"), so it needs no second form.
        rai_pilot: {
          title: 'RecoveryAI is on for %{active} of %{total} locations',
          body: 'Your %{active} RecoveryAI location resolves order issues automatically. You only step in for escalations. The other %{remaining} still sends thumbs-down straight to your inbox.',
          body_remaining_plural:
            'Your %{active} RecoveryAI location resolves order issues automatically. You only step in for escalations. The other %{remaining} still send thumbs-down straight to your inbox.',
          body_active_plural:
            'Your %{active} RecoveryAI locations resolve order issues automatically. You only step in for escalations. The other %{remaining} still sends thumbs-down straight to your inbox.',
          body_active_plural_remaining_plural:
            'Your %{active} RecoveryAI locations resolve order issues automatically. You only step in for escalations. The other %{remaining} still send thumbs-down straight to your inbox.',
          body_secondary:
            'Turn on the other %{remaining} in a couple of clicks.',
        },
        rai_all_locations: {
          title: 'RecoveryAI is on across all locations',
          body: "Every order issue is intercepted and resolved automatically. The only cases that reach you are the ones RecoveryAI escalates — you'll find them in the Feedback tab.",
        },
      },
      written_feedback: {
        label: 'Collect written feedback when guests rate thumbs down',
        // Shown only while the toggle is off. Present tense and factual: it
        // describes the live state, not the consequence of the click that just
        // happened, because it is also what a merchant sees on a fresh load.
        off_note: 'Comments are not currently being collected.',
      },
      alert: {
        submit_success:
          'Your changes to your Feedback collection settings are now live.',
        submit_error: 'This page could not be saved. Please try again.',
      },
    },
    // FR-55's Reply address sub-tab: whether a guest's reply goes to the
    // dashboard user who sent the message, or to one fixed address.
    reply_address: {
      title: 'Reply address',
      subtitle: "Choose where a guest's reply to your message should go.",
      // Composed around written_feedback_gate.phrase at the call site (the
      // pane wraps it in <b>), the same split written_feedback_gate.row uses
      // for its own link — react-redux-i18n interpolates strings, not React
      // nodes, so the bold word has to be assembled in JSX.
      gate_note: {
        prefix: 'Turn on',
        suffix: 'to set a reply address.',
      },
      // The third-party state's note, which REPLACES gate_note above when a
      // third-party feedback tool is connected. Same prefix/suffix split, for
      // the same reason: the provider name is bolded, and react-redux-i18n
      // interpolates strings, not React nodes. `%{provider}` is never spelled
      // out here — the pane passes the Integration Center catalogue's own
      // display name. The suffix opens on a comma with no leading space, so it
      // sits flush against the bolded name.
      third_party_gate_note: {
        prefix: 'Guests give feedback through',
        suffix: ', not Thanx, so these form settings don’t apply.',
      },
      options: {
        user: 'To the dashboard user who sent the message',
        single: 'A single address',
      },
      // Distinct from both the pane title and options.single on purpose
      // (mirrors EmailReplyRouting's own address_aria_label): reusing either
      // would give a label-text query two matching elements instead of one.
      address_aria_label: 'Reply-to email address',
      address_placeholder: 'replies@yourdomain.com',
      // Not part of v67 (R1 gap 2: the design has no validation states for
      // this field); written for the real implementation's fixed-mode publish
      // gate.
      address_error: {
        required: 'Enter a reply address.',
        invalid: 'Enter a valid email address.',
        thanx_blocked: 'Choose an address outside thanx.com.',
      },
      alert: {
        submit_success:
          'Your changes to your Reply address settings are now live.',
        submit_error: 'This page could not be saved. Please try again.',
      },
    },
    // FR-56/FR-57's Guest experience sub-tab: the "Call the store" shortcut on
    // the written-feedback form, and the message shown right after a guest
    // submits it.
    guest_experience: {
      title: 'Guest experience',
      subtitle: 'What guests see and receive around feedback.',
      // Same split as reply_address.gate_note, composed around
      // written_feedback_gate.phrase at the call site.
      gate_note: {
        prefix: 'Turn on',
        suffix: 'to customize the guest form.',
      },
      // The third-party state's note, which REPLACES gate_note above when a
      // third-party feedback tool is connected. Same prefix/suffix split, for
      // the same reason: the provider name is bolded, and react-redux-i18n
      // interpolates strings, not React nodes. The provider is never spelled
      // out here — the pane passes the Integration Center catalogue's own
      // display name. The suffix opens on a comma with no leading space, so it
      // sits flush against the bolded name.
      third_party_gate_note: {
        prefix: 'Guests give feedback through',
        suffix: ', not Thanx, so these form settings don’t apply.',
      },
      // FR-57 states the acknowledgment message "requires a minimum app
      // version today" and asks for that dependency to be flagged wherever
      // the setting is surfaced. Prototype v83 flags it on the Call-the-store
      // row only, as a one-line label with the detail in a tooltip.
      //
      // Written per platform on purpose: iOS and Android ship separately and
      // a merchant is routinely compliant on one and behind on the other
      // (illegalpetes today is iOS 54.1.1, Android 62.0.0). One combined
      // sentence would have to pick a single worst version and imply both
      // platforms were on it.
      //
      // Sentence-per-line, not a bulleted list: the lead states the rule
      // once, and each platform line answers "and where am I?" in the same
      // shape, so a merchant reading only their own platform still gets a
      // complete sentence.
      app_version_notice: {
        // The one line the merchant reads without hovering anything. Verbatim
        // from v83, trailing `+` included; `%{version}` is derived from the
        // required version CODE in the component.
        label: 'Requires app v%{version}+',
        // The tooltip trigger's accessible name. v83 leaves its `tabindex`
        // span unnamed; a name is this repo's a11y floor, not the design's.
        info_label: 'App version requirement details',
        // `%{version}` is derived from the required version CODE in the
        // component, so the number here can never drift from the number the
        // comparison uses.
        lead: 'Only guests on app v%{version} or later see these settings.',
        // `%{platform}` is one of the labels below; `%{current}` is that
        // platform's own published version, straight from the API's display
        // string.
        behind:
          'Your %{platform} app is on v%{current}, so %{platform} guests don’t see them yet.',
        met: 'Your %{platform} app is on v%{current}, so %{platform} guests see them.',
        // Platform names. Through i18n like every other string in this file,
        // even though both happen to be untranslated brand names.
        platforms: {
          ios: 'iOS',
          android: 'Android',
        },
      },
      call_the_store: {
        // The literal double-quote characters are part of the copy (R1 sec 2.1).
        heading: '"Call the store" option',
        body: 'Adds a phone shortcut to the written-feedback form so guests can call instead of typing. Some merchants turn this off after increased call volume.',
        // Verbatim from the prototype's own `img.alt` (AI-1283 illustrations
        // follow-up) — not authored here.
        image_alt: "'Call the store' in the guest feedback form",
      },
      acknowledgment_message: {
        heading: 'Acknowledgment message',
        // En dash before "make it yours" — confirmed by zoom (R1 sec 2.1) and
        // kept verbatim; this file's own dash rule is for comments, not design
        // copy.
        body: 'Shown to the guest right after they submit feedback. Ships with a default – make it yours.',
        // Not shown as a visible counter (v67 has none, R1 sec 2.4/gap 2) —
        // announced via aria-describedby instead, so the 500-char cap enforced
        // by the textarea's native maxLength is still stated to assistive tech.
        max_length_hint: 'Maximum 500 characters.',
        // Verbatim from the prototype's own `img.alt` (AI-1283 illustrations
        // follow-up) — not authored here.
        image_alt: 'Acknowledgment the guest sees after rating',
      },
      alert: {
        submit_success:
          'Your changes to your Guest experience settings are now live.',
        submit_error: 'This page could not be saved. Please try again.',
      },
    },
    // FR-24's Notifications sub-tab: who gets emailed when guests leave
    // feedback, relocated here from OrderFeedbackNotificationsModal (deleted).
    // The recipient labels themselves (fallback/all/additional) and the
    // error/unavailable banner copy are UNCHANGED by the new design and still
    // live on notification_settings above — this block owns only the copy
    // v67 introduces or rewords. Same wart as that namespace's own comment:
    // not renamed here either, to keep the two blocks readable side by side.
    notifications: {
      title: 'Notifications',
      subtitle:
        'Choose who gets notified when guests leave feedback. You may also control which notification emails are delivered to your inbox.',
      // Composed around written_feedback_gate.phrase at the call site.
      gate_note: {
        prefix: 'Turn on',
        suffix: 'to be notified when guests leave feedback.',
      },
      location_managers: {
        heading: 'Location managers notifications',
        description:
          'Alert location managers about feedback from their own locations; anything without a manager goes to your fallback recipient.',
      },
      recipients: {
        heading: 'Recipients for all email notifications',
        // Reworded from notification_settings.additional_desc (R1 sec 3.1):
        // drops "order", "customer" and "and" for "or", so it is its own key
        // rather than a reuse.
        additional_desc:
          "Forward a copy of every feedback notification to these inboxes. Useful for ops teams tracking issues across locations, or organizations that use ticket-based tools like Zendesk. They don't replace the primary routing above.",
      },
      alert: {
        submit_success:
          'Your changes to your Notifications settings are now live.',
        // Deliberately scope-neutral, unlike the other three panes' identical
        // "This page could not be saved" copy. Notifications commits TWO writes
        // (recipients, then the notify-location-managers config) and
        // `commit()` collapses them into one boolean, so a recipients-succeeded
        // /config-failed partial reaches this same branch. Claiming the page
        // could not be saved would contradict both the persisted recipients and
        // the pane's own inline banner, which reads the controller's
        // `isNotifyOnlySaveError` and is correctly scoped. This sends the
        // merchant to that banner rather than asserting a scope it cannot know.
        submit_error:
          'Some of your notification settings could not be saved. Check the details on this page.',
      },
    },
    // FR-58's dependent-control gating copy (WrittenFeedbackGateNote). This
    // contract owns only the shared bold phrase every pane-level sentence
    // wraps around, plus the one gate instance that ships with AI-1267 rather
    // than with a pane. Feedback collection, Reply address, Guest experience
    // and Notifications all exist now and each composes its own sentence
    // around `phrase` below; Guest experience also reuses `row` below for its
    // nested Call-the-store note.
    written_feedback_gate: {
      phrase: 'written feedback',
      // The nested Call-the-store note (R1 sec 2.1), verbatim from v67. Wired
      // into a real row by GuestExperiencePane's own Call-the-store gate.
      row: {
        body: 'Guests reach this from the written-feedback form, so it’s unavailable while written feedback is off.',
        link: 'Turn it on',
        suffix: 'to offer it.',
      },
    },
  },
  toolbar: {
    search_aria: 'Search order feedback by customer name',
    hide_resolved: 'Hide resolved',
  },
  // Passed to FeedbackTable as plain strings; the table picks empty vs
  // filtered_empty by whether any filter is active.
  table: {
    empty: 'No order feedback to show yet.',
    filtered_empty: 'No order feedback matches your filters.',
    error: 'We couldn’t load order feedback. Please try again.',
  },
  // The detail page for a rating with no RecoveryAI conversation
  // (/reports/feedback/ratings/:ratingId). Deliberately mirrors the RAI case
  // detail's copy where the two pages show the same thing; the promoted
  // components (status badge, order summary, receipt, message list) bring their
  // own strings from the `components.*` bundles.
  rating_detail: {
    back: 'Back',
    // The header "‹ Case N of M unresolved ›" pager (AI-1023). prev/next are
    // icon-button aria-labels.
    pager: {
      label: 'Case %{position} of %{total} unresolved',
      prev: 'Previous unresolved case',
      next: 'Next unresolved case',
    },
    empty: '—',
    loading: 'Loading response…',
    error: 'We couldn’t load this response. Please try again.',
    retry: 'Retry',
    not_found: 'We couldn’t find that response.',
    // The header title: human order number when the order snapshot has one
    // (DATA-4857 means it's producer-nil today — see RatingDetail's `title`),
    // else the customer's name.
    order_heading: 'Order #%{number}',
    // Accessible name for the placeholder that stands in for the title while
    // the order snapshot loads, so the customer-name fallback doesn't flash.
    order_heading_loading: 'Loading order',
    // The customer's comment, restructured (redesign phase 3) out of a
    // Customer-comment tab into an always-visible card: a blockquote of the
    // comment.
    customer_response: {
      heading: 'Customer response',
    },
    details: {
      heading: 'Case details',
      date_time: 'Date & time',
      location: 'Ordering location',
    },
    // The Case timeline card (redesign phase 3): a synthesized 2-step
    // stepper (see synthesizeTimeline) — nothing here comes from a backend
    // timeline field, which an OrderRating doesn't have.
    timeline: {
      heading: 'Case timeline',
      step_submitted: 'Customer submitted complaint',
      step_awaiting: {
        title: 'Awaiting response',
        description: 'No response action recorded yet',
      },
      step_resolved: {
        title: 'Resolved',
        description: 'Resolved by %{resolver}',
      },
      // An operator's close is neutral — it mirrors the header's
      // closed_by_operator badge, not the AI's "Resolved" step (the wire
      // can't tell a dismissal from a real resolution). See synthesizeTimeline.
      step_closed: {
        title: 'Closed',
        description: 'Closed by %{resolver}',
        description_no_name: 'Closed by operator',
      },
    },
    // `resolved_by_ai` is reused by the Case timeline's resolved step (the
    // old standalone "Resolution details" card the redesign replaced named
    // the AI resolver the same way).
    resolution: {
      resolved_by_ai: 'RecoveryAI',
    },
    // The shared components/RecentActivitiesTable owns the panel's chrome
    // (`components.recent_activities`); only the per-row View label is
    // surface-specific — here it names a prior RATING, dated because a rating
    // carries no order number of its own.
    recent_activities: {
      view_aria: 'View response from %{date}',
      // Per-row Date-column tooltip. Surface-specific (the RAI case detail's
      // Date column shows a prior CASE date, not a feedback submission), so
      // each caller supplies its own copy rather than the shared bundle.
      date_tooltip: 'The date this response was submitted.',
    },
    // Operator actions in the page header. Same wording as the RAI case
    // detail's (`recovery_ai.case_detail.actions`) for the two actions this
    // surface shares — "Restrict customer" is deliberately not offered here.
    actions: {
      grant_reward: 'Grant reward',
      send_message: 'Send message',
      mark_resolved: 'Mark as resolved',
    },
    resolve: {
      // Inline error when the resolve mutation fails. Shared by Grant reward
      // and Mark as resolved; analogous to the case detail's `resolve.error`,
      // reworded for the record this page shows.
      error: 'Something went wrong resolving this response. Please try again.',
    },
    send_message: {
      // Success flash — sending never resolves/mutates the rating, so the page
      // is unchanged afterward; this is the operator's only confirmation.
      // Interim "on its way" wording (not "has been sent"): a 200 means the
      // email was enqueued, not delivered — the send happens async and can
      // still fail silently. Revisit once delivery status is exposed (AI-1030 /
      // RAI send-message delivery-truth follow-up).
      success: 'Your message is on its way to the customer.',
      // Inline error when the send-only mutation fails — distinct from
      // resolve.error above, since sending never resolves this rating.
      error: 'Something went wrong sending this message. Please try again.',
    },
    order_unavailable: 'Order details unavailable for this response.',
    // Distinct from `order_unavailable`: this is a failed fetch (retryable),
    // not a rating that settled with no order snapshot.
    order_error: 'We couldn’t load order details for this response.',
    comment_empty: 'This customer didn’t leave a comment.',
  },
  billboard: {
    // Reads as the card's headline (the promoted Billboard names the slot `eyebrow`).
    eyebrow:
      'Recover unhappy customers automatically — and win back the revenue walking out the door.',
    cta: 'Explore RecoveryAI',
    // ROI stats row (Figma 790:14527). `stat_suffix` is the shared "/month" tag
    // appended after both formatted dollar values; the two labels below name
    // which useROICalculator figure each stat block shows.
    stat_suffix: '/month',
    stat_labor_savings_label: 'Est. labor cost saved',
    stat_revenue_recovered_label: 'Est. revenue recovered',
  },
}

export default orderRatings
